import { IEntityModificationDto } from "@kuika/kuika-cl-model";

/**
 * The shared seam behind the "Recently modified" tab that every designer landing opens on,
 * and behind the "Last modified" column both of its tabs render.
 *
 * One rule decides what the tab contains: an asset the entity-modification feed dates
 * inside the window. The feed is the only source — a landing never guesses a date from the
 * row it already holds, because the eight list endpoints disagree about which timestamps
 * they carry, and half of them carry none.
 *
 * The feed may report more than a landing shows (the server returns everything its list
 * endpoint's scope allows, so a row can never go missing); the landing intersects by id and
 * the surplus falls away here.
 */

/** The tab's label and test id, identical on all eight landings. */
export const RECENTLY_MODIFIED_TAB: string = "Recently modified";
export const RECENTLY_MODIFIED_TAB_TEST_ID: string = "recentlyModifiedTab";

/** How far back "recently" reaches. */
export const RECENTLY_MODIFIED_WINDOW_DAYS: number = 30;

const MS_PER_DAY: number = 24 * 60 * 60 * 1000;

/** One feed entry with its instant already parsed, so the window and the sort agree. */
export interface IRecentlyModifiedEntry {
  modifiedAt: number;
  modifiedByName?: string;
  modifiedByPhotoUrl?: string;
}

/** Feed entries by entity id — a landing looks rows up by the id it already has. */
export type ModificationIndex = ReadonlyMap<string, IRecentlyModifiedEntry>;

/**
 * Turns a fetched feed into the lookup a landing uses. An absent feed — not fetched yet, or
 * a failed fetch — yields an empty index rather than throwing, which is what lets the full
 * list keep rendering when the call goes wrong.
 */
export const buildModificationIndex = (feed: readonly IEntityModificationDto[] | undefined): ModificationIndex => {
  const index: Map<string, IRecentlyModifiedEntry> = new Map<string, IRecentlyModifiedEntry>();
  if (!feed) return index;

  for (const dto of feed) {
    const modifiedAt: number = Date.parse(dto.modifiedAt);
    // An unparseable instant is dropped, not kept as epoch zero: zero would read as
    // "modified in 1970", which sorts last but still claims to be a date.
    if (Number.isNaN(modifiedAt)) continue;

    const existing: IRecentlyModifiedEntry | undefined = index.get(dto.entityId);
    if (existing && existing.modifiedAt >= modifiedAt) continue;

    index.set(dto.entityId, {
      modifiedAt,
      modifiedByName: dto.modifiedByName,
      modifiedByPhotoUrl: dto.modifiedByPhotoUrl
    });
  }

  return index;
};

export const lookupModification = (
  index: ModificationIndex,
  entityId: string | undefined
): IRecentlyModifiedEntry | undefined => (entityId ? index.get(entityId) : undefined);

/** What `selectRecentlyModified` needs to know about one landing's rows. */
export interface IRecentlyModifiedQuery<T> {
  items: readonly T[];
  index: ModificationIndex;
  getId: (item: T) => string | undefined;
  getName: (item: T) => string;
  now: number;
}

const isInsideWindow = (entry: IRecentlyModifiedEntry, now: number): boolean =>
  now - entry.modifiedAt <= RECENTLY_MODIFIED_WINDOW_DAYS * MS_PER_DAY;

/**
 * Explicit "en" locale so two machines with different system locales order the tab
 * identically — the same reason the process gallery pins it.
 */
const compareByName = <T>(a: T, b: T, getName: (item: T) => string): number =>
  getName(a).localeCompare(getName(b), "en", { sensitivity: "base" });

/**
 * Whether anything at all is recent — the question that decides which tab a landing opens on.
 *
 * Deliberately separate from `selectRecentlyModified`, and deliberately asked of the landing's
 * WHOLE list rather than its filtered one. Asking the filtered list would let typing in the
 * search box move the user to another tab, because a query that matches nothing recent would
 * read as "nothing is recent".
 */
export const hasRecentlyModified = <T>(
  items: readonly T[],
  index: ModificationIndex,
  getId: (item: T) => string | undefined,
  now: number
): boolean =>
  items.some((item: T): boolean => {
    const entry: IRecentlyModifiedEntry | undefined = lookupModification(index, getId(item));
    if (!entry) return false;
    return isInsideWindow(entry, now);
  });

/** The tab's rows: everything the feed dates inside the window, newest first. */
export const selectRecentlyModified = <T>(query: IRecentlyModifiedQuery<T>): T[] => {
  const recent: T[] = query.items.filter((item: T): boolean => {
    const entry: IRecentlyModifiedEntry | undefined = lookupModification(query.index, query.getId(item));
    if (!entry) return false;
    return isInsideWindow(entry, query.now);
  });

  return recent.sort((a: T, b: T): number => {
    const left: IRecentlyModifiedEntry | undefined = lookupModification(query.index, query.getId(a));
    const right: IRecentlyModifiedEntry | undefined = lookupModification(query.index, query.getId(b));
    // Both are present — the filter above kept only dated rows.
    const difference: number = (right?.modifiedAt ?? 0) - (left?.modifiedAt ?? 0);
    return difference === 0 ? compareByName(a, b, query.getName) : difference;
  });
};

/**
 * "Ada Lovelace" → "AL", "Ada" → "AD" for the avatar shown beside a writer's name. Uppercased
 * with the locale-independent `toUpperCase` so a Turkish system locale cannot turn an ASCII
 * name into a dotted İ.
 */
export const initialsFromName = (name: string | undefined): string => {
  const trimmed: string = (name ?? "").trim();
  if (!trimmed) return "";
  const parts: string[] = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Where the modification feed has got to. */
export type FeedStatus = "loading" | "ready" | "failed";

export interface IActiveTabInput {
  pickedTab: string | null;
  feedStatus: FeedStatus;
  /**
   * Whether anything is recent. Compute it with `hasRecentlyModified` over the landing's whole
   * list — a value derived from the searched or filtered list makes the tab move while typing.
   */
  hasRecent: boolean;
  /**
   * Whether the landing's own list has arrived. A landing that fetches its rows can have the feed
   * answer first, and deciding then would read "nothing is recent" off an empty list and drop to
   * the full tab, only to jump back when the rows land. Defaults to true for the landings whose
   * rows are already in the provider by the time they mount.
   */
  itemsReady?: boolean;
  recentTab: string;
  fallbackTab: string;
}

/**
 * Which tab a landing shows. Derived rather than stored, so the answer cannot go stale
 * against the feed — a landing keeps only what the user picked.
 *
 * A failed feed resolves to the full list: the "Recently modified" tab is the default, never
 * a gate, and one bad call must not hide a tool's primary list. While the feed loads the
 * answer stays on the recent tab — the landing shows its loading presentation, and
 * resolving to the full list here would flip the tab the instant the feed arrived.
 */
export const resolveActiveTab = (input: IActiveTabInput): string => {
  if (input.pickedTab !== null) return input.pickedTab;
  if (input.feedStatus === "failed") return input.fallbackTab;
  if (input.feedStatus === "loading") return input.recentTab;
  if (input.itemsReady === false) return input.recentTab;
  return input.hasRecent ? input.recentTab : input.fallbackTab;
};
